import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCustomer } from '../hooks/useCustomer';
import './Customer.css';

const Customer: React.FC = () => {
  const { t } = useTranslation('customer');
  const { customerData, loading, error, updateCustomer, refetchCustomer } = useCustomer();
  
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    termsOfUse: '',
    privacyPolicy: '',
    emailVerificationAcceptedTitle: '',
    emailVerificationAcceptedFromName: '',
    emailVerificationAcceptedContent: '',
    emailVerificationCompletedTitle: '',
    emailVerificationCompletedFromName: '',
    emailVerificationCompletedContent: '',
  });

  useEffect(() => {
    if (customerData) {
      setFormData({
        termsOfUse: customerData.termsOfUse || '',
        privacyPolicy: customerData.privacyPolicy || '',
        emailVerificationAcceptedTitle: customerData.emailVerificationAcceptedTitle || '',
        emailVerificationAcceptedFromName: customerData.emailVerificationAcceptedFromName || '',
        emailVerificationAcceptedContent: customerData.emailVerificationAcceptedContent || '',
        emailVerificationCompletedTitle: customerData.emailVerificationCompletedTitle || '',
        emailVerificationCompletedFromName: customerData.emailVerificationCompletedFromName || '',
        emailVerificationCompletedContent: customerData.emailVerificationCompletedContent || '',
      });
    }
  }, [customerData]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    try {
      await updateCustomer(formData);
      setIsEditMode(false);
      refetchCustomer();
    } catch (error) {
      console.error('Error saving customer information:', error);
    }
  };

  const handleCancel = () => {
    if (customerData) {
      setFormData({
        termsOfUse: customerData.termsOfUse || '',
        privacyPolicy: customerData.privacyPolicy || '',
        emailVerificationAcceptedTitle: customerData.emailVerificationAcceptedTitle || '',
        emailVerificationAcceptedFromName: customerData.emailVerificationAcceptedFromName || '',
        emailVerificationAcceptedContent: customerData.emailVerificationAcceptedContent || '',
        emailVerificationCompletedTitle: customerData.emailVerificationCompletedTitle || '',
        emailVerificationCompletedFromName: customerData.emailVerificationCompletedFromName || '',
        emailVerificationCompletedContent: customerData.emailVerificationCompletedContent || '',
      });
    }
    setIsEditMode(false);
  };

  if (loading) {
    return <div className="loading">{t('loading')}</div>;
  }

  if (error) {
    const errorMessage = typeof error === 'string' ? error : error.message;
    return <div className="error">{t('error')}: {errorMessage}</div>;
  }

  return (
    <div className="customer">
      <div className="header">
        <h1>{t('title')}</h1>
        <div className="actions">
          {!isEditMode ? (
            <button onClick={() => setIsEditMode(true)} className="edit-button">
              {t('edit')}
            </button>
          ) : (
            <div className="edit-actions">
              <button onClick={handleSave} className="save-button">
                {t('save')}
              </button>
              <button onClick={handleCancel} className="cancel-button">
                {t('cancel')}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="content">
        <section className="legal-section">
          <h2>{t('legalContent')}</h2>
          <div className="form-group">
            <label>{t('termsOfUse')}</label>
            {isEditMode ? (
              <textarea
                value={formData.termsOfUse}
                onChange={(e) => handleInputChange('termsOfUse', e.target.value)}
                rows={6}
              />
            ) : (
              <div className="readonly-content">{formData.termsOfUse || t('notSet')}</div>
            )}
          </div>
          <div className="form-group">
            <label>{t('privacyPolicy')}</label>
            {isEditMode ? (
              <textarea
                value={formData.privacyPolicy}
                onChange={(e) => handleInputChange('privacyPolicy', e.target.value)}
                rows={6}
              />
            ) : (
              <div className="readonly-content">{formData.privacyPolicy || t('notSet')}</div>
            )}
          </div>
        </section>

        <section className="email-verification-section">
          <h2>{t('emailVerificationAccepted')}</h2>
          <div className="form-group">
            <label>{t('emailTitle')}</label>
            {isEditMode ? (
              <input
                type="text"
                value={formData.emailVerificationAcceptedTitle}
                onChange={(e) => handleInputChange('emailVerificationAcceptedTitle', e.target.value)}
              />
            ) : (
              <div className="readonly-content">{formData.emailVerificationAcceptedTitle || t('notSet')}</div>
            )}
          </div>
          <div className="form-group">
            <label>{t('emailFromName')}</label>
            {isEditMode ? (
              <input
                type="text"
                value={formData.emailVerificationAcceptedFromName}
                onChange={(e) => handleInputChange('emailVerificationAcceptedFromName', e.target.value)}
              />
            ) : (
              <div className="readonly-content">{formData.emailVerificationAcceptedFromName || t('notSet')}</div>
            )}
          </div>
          <div className="form-group">
            <label>{t('emailContent')}</label>
            {isEditMode ? (
              <textarea
                value={formData.emailVerificationAcceptedContent}
                onChange={(e) => handleInputChange('emailVerificationAcceptedContent', e.target.value)}
                rows={4}
              />
            ) : (
              <div className="readonly-content">{formData.emailVerificationAcceptedContent || t('notSet')}</div>
            )}
          </div>
        </section>

        <section className="email-verification-section">
          <h2>{t('emailVerificationCompleted')}</h2>
          <div className="form-group">
            <label>{t('emailTitle')}</label>
            {isEditMode ? (
              <input
                type="text"
                value={formData.emailVerificationCompletedTitle}
                onChange={(e) => handleInputChange('emailVerificationCompletedTitle', e.target.value)}
              />
            ) : (
              <div className="readonly-content">{formData.emailVerificationCompletedTitle || t('notSet')}</div>
            )}
          </div>
          <div className="form-group">
            <label>{t('emailFromName')}</label>
            {isEditMode ? (
              <input
                type="text"
                value={formData.emailVerificationCompletedFromName}
                onChange={(e) => handleInputChange('emailVerificationCompletedFromName', e.target.value)}
              />
            ) : (
              <div className="readonly-content">{formData.emailVerificationCompletedFromName || t('notSet')}</div>
            )}
          </div>
          <div className="form-group">
            <label>{t('emailContent')}</label>
            {isEditMode ? (
              <textarea
                value={formData.emailVerificationCompletedContent}
                onChange={(e) => handleInputChange('emailVerificationCompletedContent', e.target.value)}
                rows={4}
              />
            ) : (
              <div className="readonly-content">{formData.emailVerificationCompletedContent || t('notSet')}</div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Customer;
